import React, { useEffect, useState, useRef } from 'react';
/**
 * SentenceErrorJudgeRenderer — Pick which sentence contains a spelling error.
 *
 * The student sees 4 sentences and taps the one with the error.
 * Data format: data.sentences = [{ text, hasError, correction?, explanation? }]
 */
import { QuestionRendererProps } from './types';
import { motion } from 'framer-motion';
import { AlertCircleIcon, CheckCircle2Icon } from 'lucide-react';
interface SentenceItem {
  text: string;
  hasError: boolean;
  correction?: string;
  explanation?: string;
}
export function SentenceErrorJudgeRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.data || {};
  const sentences: SentenceItem[] = config.sentences as SentenceItem[] || [];
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const prevValidateRef = useRef(false);
  useEffect(() => {
    setSelectedIndex(null);
    prevValidateRef.current = false;
  }, [question.id]);
  useEffect(() => {
    if (!shouldValidate) prevValidateRef.current = false;
  }, [shouldValidate]);
  useEffect(() => {
    onReadyChange?.(selectedIndex !== null);
  }, [selectedIndex, onReadyChange]);
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      onAnswer(selectedIndex !== null ? selectedIndex : -1);
    }
  }, [shouldValidate, selectedIndex, onAnswer]);
  const handleSelect = (index: number) => {
    if (shouldValidate) return;
    setSelectedIndex(index);
  };
  return <div className="space-y-3" data-id="element-7243">
      <p className="text-sm text-gray-500 flex items-center gap-1.5 mb-4" data-id="element-7244">
        <AlertCircleIcon className="w-4 h-4" data-id="element-7245" />
        Klik op de zin met de fout
      </p>
      {sentences.map((sentence, index) => {
      const isSelected = selectedIndex === index;
      return <motion.button key={index} type="button" onClick={() => handleSelect(index)} whileTap={{
        scale: 0.98
      }} className={`
              w-full text-left p-4 rounded-xl border-2 transition-all duration-150
              ${isSelected ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'}
              ${shouldValidate ? 'pointer-events-none' : 'cursor-pointer'}
            `} data-id="element-7246">
            <div className="flex items-start gap-3" data-id="element-7247">
              <span className={`
                flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold mt-0.5
                ${isSelected ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'}
              `} data-id="element-7248">
                {index + 1}
              </span>
              <span className={`text-base leading-relaxed ${isSelected ? 'text-blue-900 font-medium' : 'text-gray-800'}`} data-id="element-7249">
                {sentence.text}
              </span>
              {isSelected && <CheckCircle2Icon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5 ml-auto" data-id="element-7250" />}
            </div>
          </motion.button>;
    })}
    </div>;
}